import { c as _c } from "react/compiler-runtime";
import { useState, useId, useCallback } from 'react';
import { translateInline } from '@/globals/translate/translateInline';
import { produce } from '@/globals/helpers/immer';
import { getHasUnansweredQuestions } from '../../_data/filterQuestions';
import { Form, Fieldset, QuestionTitle, StackAnswers, AnswerContainer, Radio, FormError, GetHasUnansweredQuestionsContext, GetHasUnansweredQuestions, AnswerLabel, QuestionSubtitle } from '../currentQuestion/CurrentQuestionParts';
import { AnswerHelp, AnswerHelpButton, QuestionHelp } from '../currentQuestion/Help';
import { stack } from '@/uiPrimitives/layout/stack';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { RenderedSingleSelectQuestion, SingleSelectGivenAnswer } from './singleSelectMethods.types';
import { AnyRenderedQuestion, GivenAnswers } from '../question.types';
import { QuestionMethods } from '../questionMethods.types';
import { QuestionNumber } from '../AnsweredQuestionParts';
const getHasSomePossibleUnansweredQuestions = ({
  question,
  givenAnswers,
  allQuestions
}: {
  question: RenderedSingleSelectQuestion;
  givenAnswers: GivenAnswers;
  allQuestions: Array<AnyRenderedQuestion>;
}) => {
  const givenAnswerWithAllPossibleAnswers = question.answers.map(answer => {
    const givenAnswersWithAllCurrentAnswers = produce(givenAnswers, givenAnswers => {
      givenAnswers.single.set(question.id, answer.id);
    });
    return givenAnswersWithAllCurrentAnswers;
  });
  const hasSomePossibleUnansweredQuestions = givenAnswerWithAllPossibleAnswers.some(givenAnswersWithAllCurrentAnswers => {
    return getHasUnansweredQuestions(allQuestions, givenAnswersWithAllCurrentAnswers);
  });
  return hasSomePossibleUnansweredQuestions;
};
const getSingleHasUnansweredQuestions = ({
  allQuestions,
  givenAnswers,
  question,
  currentGivenAnswer
}: {
  allQuestions: Array<AnyRenderedQuestion>;
  givenAnswers: GivenAnswers;
  question: RenderedSingleSelectQuestion;
  currentGivenAnswer: SingleSelectGivenAnswer;
}) => {
  const hasSomePossibleUnansweredQuestions = getHasSomePossibleUnansweredQuestions({
    question,
    givenAnswers,
    allQuestions
  });
  const speculativeGivenAnswers = produce(givenAnswers, givenAnswers => {
    givenAnswers.single.set(question.id, currentGivenAnswer);
  });
  const hasUnansweredQuestions = getHasUnansweredQuestions(allQuestions, speculativeGivenAnswers);
  if (!hasSomePossibleUnansweredQuestions) {
    return hasSomePossibleUnansweredQuestions;
  }
  return hasUnansweredQuestions;
};
export const CurrentQuestionSingle: QuestionMethods<RenderedSingleSelectQuestion, SingleSelectGivenAnswer>['Current'] = t0 => {
  const $ = _c(58);
  const {
    containerId,
    titleId,
    question,
    givenAnswers,
    onSubmit,
    progressSlot,
    buttonsSlot
  } = t0;
  let t1;
  if ($[0] !== givenAnswers.single || $[1] !== question.id) {
    t1 = givenAnswers.single.get(question.id);
    $[0] = givenAnswers.single;
    $[1] = question.id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const givenAnswer = t1;
  const errorId = useId();
  const [currentGivenAnswer, setCurrentGivenAnswer] = useState(givenAnswer);
  let t2;
  if ($[3] !== currentGivenAnswer || $[4] !== question) {
    t2 = t3 => {
      const {
        allQuestions,
        givenAnswers: givenAnswers_0
      } = t3;
      return getSingleHasUnansweredQuestions({
        allQuestions,
        givenAnswers: givenAnswers_0,
        question,
        currentGivenAnswer
      });
    };
    $[3] = currentGivenAnswer;
    $[4] = question;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const getHasUnansweredQuestions = t2;
  let t3;
  if ($[6] !== currentGivenAnswer || $[7] !== onSubmit || $[8] !== question.answers || $[9] !== question.id) {
    t3 = () => onSubmit({
      givenAnswersProducer: draft => {
        draft.single.set(question.id, currentGivenAnswer ?? null);
      },
      getAnswerTitle: () => question.answers.find(answer => answer.id === currentGivenAnswer)?.titleText
    });
    $[6] = currentGivenAnswer;
    $[7] = onSubmit;
    $[8] = question.answers;
    $[9] = question.id;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = classes(stack({
      rowGap: "3 | h1"
    }));
    t5 = <QuestionNumber />;
    $[11] = t4;
    $[12] = t5;
  } else {
    t4 = $[11];
    t5 = $[12];
  }
  let t6;
  if ($[13] !== question.title || $[14] !== titleId) {
    t6 = <QuestionTitle id={titleId}>{question.title}</QuestionTitle>;
    $[13] = question.title;
    $[14] = titleId;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== question.help || $[17] !== question.id || $[18] !== question.titleText || $[19] !== t6) {
    t7 = <QuestionHelp help={question.help} titleText={question.titleText} questionId={question.id}>{t6}</QuestionHelp>;
    $[16] = question.help;
    $[17] = question.id;
    $[18] = question.titleText;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== question.subtitle) {
    t8 = Boolean(question.subtitle) && <QuestionSubtitle>{question.subtitle}</QuestionSubtitle>;
    $[21] = question.subtitle;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== currentGivenAnswer || $[24] !== errorId || $[25] !== question.answers || $[26] !== question.id || $[27] !== question.optional || $[28] !== question.titleText) {
    let t10;
    if ($[30] !== currentGivenAnswer || $[31] !== errorId || $[32] !== question.id || $[33] !== question.optional || $[34] !== question.titleText) {
      t10 = answer_0 => {
        const isChecked = currentGivenAnswer === answer_0.id;
        return <AnswerHelp help={answer_0.help} key={answer_0.id}><AnswerContainer><Radio required={!question.optional} name={question.id} checked={isChecked} onChange={t11 => {
              const {
                currentTarget
              } = t11;
              setCurrentGivenAnswer(currentTarget.checked ? answer_0.id : undefined);
            }} aria-describedby={errorId} /><AnswerLabel helpSlot={Boolean(answer_0.help) && <AnswerHelpButton titleText={answer_0.titleText} questionTitle={question.titleText} currentQuestionId={question.id} />}>{answer_0.title}</AnswerLabel></AnswerContainer></AnswerHelp>;
      };
      $[30] = currentGivenAnswer;
      $[31] = errorId;
      $[32] = question.id;
      $[33] = question.optional;
      $[34] = question.titleText;
      $[35] = t10;
    } else {
      t10 = $[35];
    }
    t9 = question.answers.map(t10);
    $[23] = currentGivenAnswer;
    $[24] = errorId;
    $[25] = question.answers;
    $[26] = question.id;
    $[27] = question.optional;
    $[28] = question.titleText;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[36] !== t9) {
    t10 = <StackAnswers>{t9}</StackAnswers>;
    $[36] = t9;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = translateInline({
      en: "Please select an answer",
      nl: "Selecteer een antwoord"
    });
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== errorId) {
    t12 = <FormError id={errorId}>{t11}</FormError>;
    $[39] = errorId;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== t10 || $[42] !== t12 || $[43] !== t7 || $[44] !== t8 || $[45] !== titleId) {
    t13 = <Fieldset aria-labelledby={titleId} role="radiogroup">{t7}{t8}{t10}{t12}</Fieldset>;
    $[41] = t10;
    $[42] = t12;
    $[43] = t7;
    $[44] = t8;
    $[45] = titleId;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  let t14;
  if ($[47] !== progressSlot || $[48] !== t13) {
    t14 = <div className={t4}>{t5}{progressSlot}{t13}</div>;
    $[47] = progressSlot;
    $[48] = t13;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  let t15;
  if ($[50] !== buttonsSlot || $[51] !== containerId || $[52] !== t14 || $[53] !== t3) {
    t15 = <Form id={containerId} onSubmit={t3}>{t14}{buttonsSlot}</Form>;
    $[50] = buttonsSlot;
    $[51] = containerId;
    $[52] = t14;
    $[53] = t3;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  let t16;
  if ($[55] !== getHasUnansweredQuestions || $[56] !== t15) {
    t16 = <GetHasUnansweredQuestionsContext.Provider value={getHasUnansweredQuestions}>{t15}</GetHasUnansweredQuestionsContext.Provider>;
    $[55] = getHasUnansweredQuestions;
    $[56] = t15;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  return t16;
};